import React, { useState } from 'react';
import { Typography, Button, Modal } from '@scale/scaleui';
import {
  AuditPermissionRole,
  GlobalWorkerSkillScorePermission,
} from '@scale/shared/taskerScreening/types';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import GlobalWorkerSkillScorePermissionsBuilder from 'react-scale/corp/views/rapid/TaskerScreeningUtils/Components/GlobalWorkerSkillScorePermissionsBuilder';
import GlobalTagsPermissionsBuilder from 'react-scale/corp/views/rapid/TaskerScreeningUtils/Components/GlobalTagsPermissionsBuilder';

interface TaskerScreeningAuditorPermissionReviewModalProps {
  role: AuditPermissionRole;
  open: boolean;
  onClose: () => void;
  onSave: (
    id: string,
    globalScoreFilters: GlobalWorkerSkillScorePermission[],
    requireTags: { label: string; value: string }[],
    excludeTags: { label: string; value: string }[],
  ) => void;
}

const PaddedHeader = ({ title }: { title: string }) => {
  return (
    <div className="mt-4 mb-2">
      <Typography.H5>{title}</Typography.H5>
    </div>
  );
};

const TaskerScreeningAuditorPermissionReviewContents: React.FC<
  TaskerScreeningAuditorPermissionReviewModalProps
> = ({ role, onSave }) => {
  const [globalScoreFilters, setGlobalScoreFilters] = useState<GlobalWorkerSkillScorePermission[]>(
    role.globalWorkerSkillScorePermissions ?? [],
  );
  const [requireTagsSelect, setRequireTagsSelect] = useState<{ label: string; value: string }[]>(
    role.globalRequiredTagLabelsValues ?? [],
  );
  const [excludeTagsSelect, setExcludeTagsSelect] = useState<{ label: string; value: string }[]>(
    role.globalExcludedTagLabelsValues ?? [],
  );
  const [useTagFilters, setUseTagFilters] = useState<boolean>(
    !!requireTagsSelect?.length || !!excludeTagsSelect?.length,
  );
  return (
    <div className="scale-ui scale-theme scale-tailwind w-[600px]">
      <PaddedHeader title="Role Name" />
      <Typography.Paragraph2>{role.name}</Typography.Paragraph2>
      {role.description && (
        <div>
          <PaddedHeader title="Role Description" />
          <Typography.Paragraph2>{role.description}</Typography.Paragraph2>
        </div>
      )}
      <PaddedHeader title="Role Courses" />
      {role.courseIds.map(courseId => (
        <div key={courseId} className="w-full">
          <SmartLink key={courseId} type={INamedObjectTypes.Course} id={courseId} />
        </div>
      ))}
      <PaddedHeader title="Role Course Section Titles" />
      <Typography.Paragraph2>{role.sectionTitles.join(', ')}</Typography.Paragraph2>
      <PaddedHeader title="Global Worker Skill Filters" />
      <GlobalWorkerSkillScorePermissionsBuilder
        globalScoreFilters={globalScoreFilters}
        setGlobalScoreFilters={setGlobalScoreFilters}
      />
      <PaddedHeader title="Global Worker Tag Filters" />
      <GlobalTagsPermissionsBuilder
        useTagFilters={useTagFilters}
        setUseTagFilters={setUseTagFilters}
        excludeTagsSelect={excludeTagsSelect}
        setExcludeTagsSelect={setExcludeTagsSelect}
        requireTagsSelect={requireTagsSelect}
        setRequireTagsSelect={setRequireTagsSelect}
      />
      <Button
        className="mt-4"
        onClick={() =>
          onSave(role.id, globalScoreFilters, requireTagsSelect ?? [], excludeTagsSelect ?? [])
        }
        variant="primary"
      >
        Save Changes
      </Button>
    </div>
  );
};

const TaskerScreeningAuditorPermissionReviewModal: React.FC<
  TaskerScreeningAuditorPermissionReviewModalProps
> = ({ open, onClose, role, onSave }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      theme="scale-theme"
      title="Audit Permission Role Global Filters"
      innerClassName="overflow-y-auto"
    >
      <TaskerScreeningAuditorPermissionReviewContents
        open={open}
        onClose={onClose}
        role={role}
        onSave={onSave}
      />
    </Modal>
  );
};

export default TaskerScreeningAuditorPermissionReviewModal;
